/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Ring2All Team
 📄 File: ChangeHistoryPage.tsx
 🗓️ Created: 2025-12-02
 🧩 Description:
    Main page for Change History with list view and filters
────────────────────────────────────────────────────────────────*/

import { useState, useMemo, useEffect } from 'react';
import { History, RefreshCw, FilterX } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { ModulePageContainer } from '../../components/layouts';
import { useNavigationStore } from '../../store/navigation';
import { useChangeHistoryList } from './hooks/useChangeHistoryList';
import { ChangeHistory } from './types/changeHistory.types';
import { ChangeHistoryGrid } from './components/ChangeHistoryGrid';
import { ChangesModal } from './components/ChangesModal';
import { Button } from '../../components/ui/button';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import FixedActionBar from '../../components/ui/FixedActionBar';

export default function ChangeHistoryPage() {
  const { t } = useI18n();
  const { setCurrentPageTitle } = useNavigationStore();
  const [selectedLog, setSelectedLog] = useState<ChangeHistory | null>(null);

  const pageTitle = useMemo(() => t('changeHistory.title'), [t]);

  useEffect(() => {
    setCurrentPageTitle(null);
    return () => setCurrentPageTitle(null);
  }, [setCurrentPageTitle]);

  const {
    data,
    loading,
    columns: baseColumns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    filters,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    handleFilterChange,
    handleClearFilters,
    handleApplyFilters,
  } = useChangeHistoryList();

  // Add custom renders to columns
  const columns = useMemo(
    () =>
      baseColumns.map((column) => {
        if (column.key === 'createdAt') {
          return {
            ...column,
            render: (_value: unknown, item: ChangeHistory) => (
              <span className="text-sm text-foreground">
                {item.createdAt
                  ? new Date(item.createdAt).toLocaleString()
                  : '—'}
              </span>
            )
          };
        }
        if (column.key === 'action') {
          return {
            ...column,
            render: (_value: unknown, item: ChangeHistory) => {
              const actionColors: Record<string, string> = {
                CREATE: 'text-green-600 dark:text-green-400',
                UPDATE: 'text-blue-600 dark:text-blue-400',
                DELETE: 'text-red-600 dark:text-red-400',
              };
              const colorClass = actionColors[item.action] || 'text-foreground';
              return (
                <span className={`text-sm font-medium ${colorClass}`}>
                  {item.action}
                </span>
              );
            }
          };
        }
        if (column.key === 'resource') {
          return {
            ...column,
            render: (_value: unknown, item: ChangeHistory) => (
              <span className="text-sm text-muted-foreground">
                {item.resource || '—'}
              </span>
            )
          };
        }
        if (column.key === 'user') {
          return {
            ...column,
            render: (_value: unknown, item: ChangeHistory) => (
              <div className="flex flex-col">
                <span className="text-sm font-medium text-foreground">
                  {item.user?.username || '—'}
                </span>
                {item.user?.fullName && (
                  <span className="text-xs text-muted-foreground">
                    {item.user.fullName}
                  </span>
                )}
              </div>
            )
          };
        }
        if (column.key === 'ipAddress') {
          return {
            ...column,
            render: (_value: unknown, item: ChangeHistory) => (
              <span className="font-mono text-sm text-muted-foreground">
                {item.ipAddress || '—'}
              </span>
            )
          };
        }
        if (column.key === 'metadata') {
          return {
            ...column,
            render: (_value: unknown, item: ChangeHistory) => (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedLog(item)}
                className="h-8 text-xs"
              >
                {t('changeHistory.viewChanges')}
              </Button>
            )
          };
        }
        return column;
      }),
    [baseColumns, t]
  );

  // Check if any filter is active
  const hasActiveFilters = useMemo(() => {
    return (
      filters.startDate !== '' ||
      filters.endDate !== '' ||
      (filters.actions.length > 0 && filters.actions.join(',') !== 'UPDATE') ||
      filters.resource !== '' ||
      filters.userId !== '' ||
      filters.domainId !== ''
    );
  }, [filters]);

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <div className="relative">
          <ChangeHistoryGrid
            data={data}
            columns={columns}
            loading={loading}
            pagination={pagination}
            searchQuery={searchQuery}
            sortBy={sortBy}
            sortOrder={sortOrder}
            filters={filters}
            onSearch={handleSearch}
            onSort={handleSort}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            onColumnToggle={handleColumnToggle}
            onFilterChange={handleFilterChange}
          />

          {/* Fixed Action Bar - Always visible */}
          <FixedActionBar
            onReset={hasActiveFilters ? handleClearFilters : undefined}
            onSave={handleApplyFilters}
            resetText={
              <>
                <FilterX className="h-4 w-4 mr-2" />
                {t('changeHistory.clearFilters')}
              </>
            }
            saveText={
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                {t('changeHistory.refresh')}
              </>
            }
            showCancel={false}
            showDelete={false}
            isSaving={loading}
          />

          {/* Changes Modal */}
          {selectedLog && (
            <ChangesModal
              isOpen={!!selectedLog}
              onClose={() => setSelectedLog(null)}
              metadata={selectedLog.metadata}
              title={t('changeHistory.changesTitle')}
            />
          )}
        </div>
      }
      formView={<div />}
    />
  );
}

